import { Col, Progress, Row, Tooltip } from "reactstrap"
import { LabelText } from "../Utils/Input"
import { FormattedNumber } from "./constants/FormatNumber"
import { useEffect, useRef, useState } from "react";

export const ResourceBalance = ({ resources, showAll = false }) => {
    const [openTooltipId, setOpenTooltipId] = useState(null);
    const [truncatedIds, setTruncatedIds] = useState({});
    const spanRef = useRef(null);
    const [maxLength, setMaxLength] = useState(12);

    function truncateMiddle(str, maxLength) {
        if (!str) return "";
        if (!maxLength || str.length <= maxLength) return str;

        const half = Math.floor((maxLength - 3) / 3); // 3 = "..."
        return str.slice(0, half) + "..." + str.slice(-half);
    }

    const displayResources = showAll ? resources : [resources];
    const grayTheme = {
        backgroundColor: "rgba(103, 103, 103, .12)",
        color: "#000000",
        border: "1px solid #676767",
        padding: "2px 8px",
        borderRadius: "4px",
    };
    const truncateWithTooltip = (text, maxLength = 30) => {
        if (text.length <= maxLength) return text;
        return `${text.substring(0, maxLength)}...`;
    };
    useEffect(() => {
        // After render, check which spans are truncated
        const checkTruncations = () => {
            const newTruncated = {};
            displayResources.forEach((resource, index) => {
                const safeId = `remaining-${index}`;
                const el = document.getElementById(safeId);
                if (el && el.scrollWidth > el.clientWidth) {
                    newTruncated[safeId] = true;
                }
            });
            setTruncatedIds(newTruncated);
        };

        checkTruncations();
        window.addEventListener("resize", checkTruncations);
        return () => window.removeEventListener("resize", checkTruncations);
    }, [displayResources]);

    useEffect(() => {
        if (!spanRef.current) return;

        const updateLength = () => {
            const width = spanRef.current.offsetWidth - 1; // available px
            // Approx 8px per char (adjust based on font)
            const chars = Math.floor(width / 8.5);
            setMaxLength(chars > 5 ? chars : 5); // at least 5 chars
        };

        updateLength();

        const resizeObserver = new ResizeObserver(updateLength);
        resizeObserver.observe(spanRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    const toggleTooltip = (id) => {
        // Only allow toggle if this id is actually truncated
        // if (truncatedIds[id]) {
        setOpenTooltipId(openTooltipId === id ? null : id);
        // }
    };
    return (
        <div>
            <div className="d-flex mt-n1 mb-2">
                <LabelText displayText={"Resource Balances"} />
            </div>
            <div className="">
                <div className="row">
                    {displayResources.map((resource, index) => {
                        const remainingId = `remaining-${index}`;
                        const consumedId = `consumed-${index}`;
                        const reseveredId = `reserved-${index}`;
                        return (
                            <div key={index} className={`${showAll ? 'col-12 col-sm-6 col-md-4' : 'col-12'} mb-2`}>
                                <div className="border round border-1">
                                    <div className="card-body">
                                        <Row className="align-items-start mb-3">
                                            {/* Resource name */}
                                            <Col xs="12" lg="8" className="mb-1 mb-lg-0">
                                                <p
                                                    id={`name-${index}`}
                                                    className="mb-0 text-truncate"
                                                    style={{ minWidth: 0 }}
                                                    title={resource.resourceName || ''}
                                                >
                                                    {resource.resourceName}
                                                </p>
                                            </Col>

                                            {/* Badge */}
                                            <Col
                                                xs="12"
                                                lg="4"
                                                className="d-flex justify-content-lg-end justify-content-start"
                                            >
                                                <small
                                                    className="text-nowrap text-center"
                                                    style={{
                                                        backgroundColor: grayTheme.bg,
                                                        color: grayTheme.color,
                                                        border: grayTheme.border,
                                                        padding: "2px 8px",
                                                        borderRadius: "4px",
                                                    }}
                                                >
                                                    {resource.isAddOn ? "Add-On" : "Base-Offer"}
                                                </small>
                                            </Col>
                                        </Row>



                                        {/* Resource Type Info */}
                                        <div className="mb-2">

                                        </div>

                                        {/* Reserved Balance */}
                                        <Row className="align-items-start mb-3">
                                            <Col xs="12" lg="3" className="mb-1 mb-lg-0">
                                                <LabelText displayText="Reserved" />
                                            </Col>
                                            <Col xs="12" lg="9" className="d-flex justify-content-end balance-reserved-color align-items-center" style={{ minWidth: 0 }}>
                                                {/* <FormattedNumber
                                                    value={resource?.reservedBalance+'9999999999999999988' || "0"}
                                                    uom={resource.uom || resource.currencySymbol}
                                                    maxLength={maxFormatLength}
                                                    isTruncateMiddle={true}
                                                /> */}

                                                {/* Number with middle ellipsis */}
                                                <span
                                                    id={reseveredId}
                                                    ref={spanRef}
                                                    className="text-end flex-grow-1 me-1"
                                                    style={{
                                                        minWidth: 0,
                                                        overflow: "hidden",
                                                        whiteSpace: "nowrap",
                                                        textOverflow: "clip",
                                                    }}
                                                >
                                                    {resource.currencySymbol ? resource?.reservedBalance?.toFixed(2) : truncateMiddle((resource?.reservedBalance || "0")?.toString(), maxLength, resource.uom || resource.currencySymbol)}
                                                </span>

                                                {/* Unit */}
                                                <span className="fs-6 text-nowrap">
                                                    {resource.uom || resource.currencySymbol}
                                                </span>

                                                {/* Tooltip with full value */}
                                                <Tooltip
                                                    isOpen={openTooltipId === reseveredId}
                                                    target={`#${reseveredId}`}
                                                    toggle={() => toggleTooltip(reseveredId)}
                                                    placement="top"
                                                >
                                                    {resource?.reservedBalance || "0"}
                                                </Tooltip>
                                            </Col>
                                        </Row>

                                        {/* Consumed Balance */}
                                        <Row className="align-items-start mb-3">
                                            <Col xs="12" lg="3" className="mb-1 mb-lg-0">
                                                <LabelText displayText="Consumed" />
                                            </Col>
                                            <Col xs="12" lg="9" className="d-flex justify-content-end balance-consumed-color align-items-center" style={{ minWidth: 0 }}>
                                                {/* <FormattedNumber
                                                    value={resource?.consumedBalance+'9999999999999999988'|| "0"}
                                                    uom={resource.uom || resource.currencySymbol}
                                                    maxLength={maxFormatLength}
                                                    isTruncateMiddle={true}
                                                /> */}
                                                {/* Number with middle ellipsis */}
                                                <span
                                                    id={consumedId}
                                                    ref={spanRef}
                                                    className="text-end flex-grow-1 me-1"
                                                    style={{
                                                        minWidth: 0,
                                                        overflow: "hidden",
                                                        whiteSpace: "nowrap",
                                                        textOverflow: "clip",
                                                    }}
                                                >
                                                    {resource.currencySymbol ? resource?.consumedBalance?.toFixed(2) : truncateMiddle((resource?.consumedBalance || "0")?.toString(), maxLength, resource.uom || resource.currencySymbol)}
                                                </span>

                                                {/* Unit */}
                                                <span className="fs-6 text-nowrap">
                                                    {resource.uom || resource.currencySymbol}
                                                </span>

                                                {/* Tooltip with full value */}
                                                <Tooltip
                                                    isOpen={openTooltipId === consumedId}
                                                    target={`#${consumedId}`}
                                                    toggle={() => toggleTooltip(consumedId)}
                                                    placement="top"
                                                >
                                                    {resource?.consumedBalance || "0"}
                                                </Tooltip>
                                            </Col>
                                        </Row>

                                        {/* Remaining Balance */}
                                        <Row className="align-items-start mb-3">
                                            {/* Label */}
                                            <Col xs="12" lg="3" className="mb-1 mb-lg-0">
                                                <LabelText displayText="Remaining" />
                                            </Col>

                                            {/* Value */}
                                            <Col
                                                xs="12"
                                                lg="9"
                                                className="balance-remaining-color d-flex justify-content-end align-items-center"
                                                style={{ minWidth: 0 }}
                                            >
                                                {/* <FormattedNumber
                                                    value={resource?.remainingBalance+'9999999999999999988'|| "0"}
                                                    uom={resource.uom || resource.currencySymbol}
                                                    maxLength={maxFormatLength}
                                                    isTruncateMiddle={true}
                                                /> */}
                                                {/* Number with middle ellipsis */}
                                                <span
                                                    id={remainingId}
                                                    ref={spanRef}
                                                    className="text-end flex-grow-1"
                                                    style={{
                                                        minWidth: 0,
                                                        overflow: "hidden",
                                                        whiteSpace: "nowrap",
                                                        textOverflow: "clip",
                                                    }}
                                                >
                                                    {resource.currencySymbol ? resource?.remainingBalance?.toFixed(2) : truncateMiddle((resource?.remainingBalance || "0")?.toString(), maxLength, resource.uom || resource.currencySymbol)}
                                                </span>

                                                {/* Unit */}
                                                <span className="fs-6 ms-1 text-nowrap">
                                                    {resource.uom || resource.currencySymbol}
                                                </span>

                                                {/* Tooltip with full value */}
                                                <Tooltip
                                                    isOpen={openTooltipId === remainingId}
                                                    target={`#${remainingId}`}
                                                    toggle={() => toggleTooltip(remainingId)}
                                                    placement="top"
                                                >
                                                    {resource?.remainingBalance || "0"}
                                                </Tooltip>
                                            </Col>
                                        </Row>

                                        <Progress
                                            max={resource?.originalBalance || 100}
                                            value={resource?.remainingBalance || 100}
                                        />

                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}